import { useAccount } from '../../../../../common/useAccount'
import { InputParameterHierarchyType } from '../../../../../components/InputParameterHierarchy/model'
import { LOCK_ALL_OVERRIDE } from '../../../../../models/Account'
import { InputParametersHierarchyLevel } from '../../../../../models/InputParametersHierarchy'
import { type ReminderSettings, type ReminderType } from '../../../../../models/Scheduling'
import { type TemplateLookup } from '../../../../../models/TemplateLibrary'
import {
  useCreateNodeMutation,
  useDeleteNodeMutation,
  useListAccountNodesQuery,
  useListReminderTemplateChoicesQuery,
  useUpdateNodeMutation,
  type ReminderTemplateChoices,
} from '../../../../../store/ai/inputParametersHierarchy'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { schedulingApi } from '../../../../../store/scheduling/scheduling'
import { useSchedulingEmails } from '../../../Templates/SchedulingEmails/useSchedulingEmails'
import { REMINDER_EMAIL_SLUG } from '../../../../Scheduling/shared/schedulingEmailCatalog'
import { type TemplateWrite } from './reminderTemplates'

const TYPE = InputParameterHierarchyType.SCHEDULING_EMAIL_TEMPLATE

export interface ReminderRowTemplate {
  template?: TemplateLookup
  isUnavailable: boolean
}

export const useReminderTemplates = (sections: ReminderSettings[]) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const entries = sections.flatMap((section) =>
    section.entries.flatMap((entry) => (entry.id != null ? [{ reminderEntryId: entry.id, typeId: REMINDER_EMAIL_SLUG[section.reminderType] }] : [])),
  )
  const { account, isLoading: isAccountLoading } = useAccount()
  const isOverrideLocked = account?.enterpriseConfig.lockTemplateOverride === LOCK_ALL_OVERRIDE
  const choicesQuery = useListReminderTemplateChoicesQuery(
    { accountId, type: TYPE, entries },
    { skip: entries.length === 0 || isAccountLoading || isOverrideLocked },
  )
  const globalChoicesQuery = useListAccountNodesQuery(
    { accountId, type: TYPE, level: InputParametersHierarchyLevel.GLOBAL, effective: true },
    { skip: entries.length === 0 || !isOverrideLocked },
  )
  const schedulingEmails = useSchedulingEmails({ refetchOnMountOrArgChange: 60 })
  const [createNode] = useCreateNodeMutation()
  const [updateNode] = useUpdateNodeMutation()
  const [deleteNode] = useDeleteNodeMutation()

  const globalChoices = globalChoicesQuery.data ?? {}
  const choices: ReminderTemplateChoices = isOverrideLocked
    ? entries.reduce<ReminderTemplateChoices>(
        (lockedChoices, { reminderEntryId, typeId }) =>
          globalChoices[typeId] != null ? { ...lockedChoices, [reminderEntryId]: globalChoices[typeId] } : lockedChoices,
        {},
      )
    : (choicesQuery.data ?? {})
  const templates = schedulingEmails.templates ?? []

  const emailFor = (reminderType: ReminderType) => schedulingEmails.emails.find((email) => email.slug === REMINDER_EMAIL_SLUG[reminderType])

  const zocksDefault = (reminderType: ReminderType) =>
    templates.find((template) => template.typeId === REMINDER_EMAIL_SLUG[reminderType] && template.scope === 'system')

  const templateFor = (reminderType: ReminderType, row: { id: string | null; templateId?: string }): ReminderRowTemplate => {
    const templateId = row.templateId ?? (row.id != null ? choices[row.id]?.templateId : undefined)
    if (templateId == null) {
      return { template: zocksDefault(reminderType), isUnavailable: false }
    }
    const template = templates.find((candidate) => candidate.id === templateId)
    return { template, isUnavailable: template == null }
  }

  const isEnforced = (row: { id: string | null }) => row.id != null && choices[row.id]?.isEnterpriseEnforced === true

  const isZocksDefault = (templateId: string) => templates.some((template) => template.id === templateId && template.scope === 'system')

  const writeTemplates = async (reminderType: ReminderType, writes: TemplateWrite[]): Promise<string[]> => {
    const request = { accountId, type: TYPE, typeId: REMINDER_EMAIL_SLUG[reminderType] }
    const results = await Promise.allSettled(
      writes.map((write) => {
        if (write.kind === 'delete') {
          return deleteNode({ ...request, nodeId: write.nodeId }).unwrap()
        }
        if (write.kind === 'update') {
          return updateNode({ ...request, nodeId: write.nodeId, node: { templateId: write.templateId, active: true } }).unwrap()
        }
        return createNode({
          ...request,
          node: {
            hierarchyId: { level: InputParametersHierarchyLevel.SCHEDULING_REMINDER_GLOBAL, reminderEntryId: write.reminderEntryId },
            templateId: write.templateId,
            active: true,
          },
        }).unwrap()
      }),
    )
    const failedRowKeys = writes.filter((_, index) => results[index].status === 'rejected').map((write) => write.rowKey)
    if (writes.length > 0) {
      dispatch(schedulingApi.util.invalidateTags(['SchedulingEmailTemplate']))
    }
    if (failedRowKeys.length > 0 && entries.length > 0) {
      void choicesQuery.refetch()
    }
    return failedRowKeys
  }

  return {
    choices,
    emailFor,
    templateFor,
    isEnforced,
    isZocksDefault,
    writeTemplates,
    isLoading: isAccountLoading || choicesQuery.isLoading || globalChoicesQuery.isLoading || schedulingEmails.isLoading,
    isError: choicesQuery.isError || globalChoicesQuery.isError || schedulingEmails.isError,
  }
}
